import { useMemo, useState } from "react";

import { Boxes, Loader2, Package, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SoftwareInstanceFormDialog } from "@/features/servers/components/SoftwareInstanceFormDialog";
import {
  useHosts,
  useSoftwareInstanceMutations,
  useSoftwareInstances,
} from "@/features/servers/hooks/useCenterInfra";
import { useSoftwareTypes } from "@/features/servers/hooks/useSoftwareTypes";
import type {
  CreateSoftwareInstanceInput,
  ISoftwareInstance,
} from "@/features/servers/schemas/servers";
import { cn } from "@/shared/lib/utils";

interface SoftwareSectionProps {
  institutionUid: string;
}

export function SoftwareSection({ institutionUid }: SoftwareSectionProps) {
  const { data: instances = [], isLoading } = useSoftwareInstances(institutionUid);
  const { data: hosts = [] } = useHosts(institutionUid);
  const { data: softwareTypes = [] } = useSoftwareTypes();
  const { create, update, remove } = useSoftwareInstanceMutations(institutionUid);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ISoftwareInstance | null>(null);

  const typeById = useMemo(() => {
    const map = new Map(softwareTypes.map((t) => [t.id, t]));
    for (const t of softwareTypes) map.set(t.key, t);
    return map;
  }, [softwareTypes]);

  const hostById = useMemo(
    () => new Map(hosts.map((h) => [h.id, h])),
    [hosts]
  );

  const resolveTypeName = (softwareTypeId: string) => {
    return typeById.get(softwareTypeId)?.displayName ?? softwareTypeId;
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (instance: ISoftwareInstance) => {
    setEditing(instance);
    setDialogOpen(true);
  };

  const handleSubmit = async (data: CreateSoftwareInstanceInput) => {
    try {
      if (editing) {
        await update.mutateAsync({ instanceId: editing.id, data });
        toast.success("Software actualizado");
      } else {
        await create.mutateAsync(data);
        toast.success("Software agregado");
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Error al guardar software"
      );
      throw error;
    }
  };

  const handleDelete = async (instance: ISoftwareInstance) => {
    const name = resolveTypeName(instance.softwareTypeId);
    if (!window.confirm(`¿Eliminar la instancia "${name}"?`)) return;
    try {
      await remove.mutateAsync(instance.id);
      toast.success("Instancia eliminada");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al eliminar");
    }
  };

  return (
    <Card className="gap-3 py-4 shadow-none">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 px-4 pb-0 border-b [.border-b]:pb-3">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <Boxes className="h-4 w-4 text-muted-foreground" aria-hidden />
            Software / Servicios
            <Badge variant="secondary">{instances.length}</Badge>
          </CardTitle>
          <CardDescription className="text-xs mt-1">
            Instancias del catálogo con host, credenciales y endpoints DICOM
          </CardDescription>
        </div>
        <Button
          size="sm"
          className="cursor-pointer shrink-0"
          onClick={openCreate}
          disabled={softwareTypes.length === 0}
        >
          <Plus className="h-4 w-4 mr-1" aria-hidden />
          Software
        </Button>
      </CardHeader>
      <CardContent className="px-4 pt-3">
        {isLoading ? (
          <div className="flex justify-center py-8" role="status">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : instances.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Sin software. Agregá PACS, RIS, laboratorio, etc. según el centro.
          </p>
        ) : (
          <ul className="space-y-2">
            {instances.map((instance) => {
              const host = instance.hostId
                ? hostById.get(instance.hostId)
                : undefined;
              const typeLabel = resolveTypeName(instance.softwareTypeId);
              return (
                <li
                  key={instance.id}
                  className={cn(
                    "flex items-start justify-between gap-3 rounded-lg border border-l-4 p-3 transition-colors hover:bg-muted/20",
                    instance.verifiedAt
                      ? "border-l-emerald-500"
                      : "border-l-amber-500"
                  )}
                >
                  <div className="min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Package
                        className="size-3.5 text-muted-foreground shrink-0"
                        aria-hidden
                      />
                      <p className="font-medium text-sm">{typeLabel}</p>
                      <Badge variant={instance.active ? "default" : "secondary"}>
                        {instance.active ? "Activo" : "Inactivo"}
                      </Badge>
                      {instance.version && (
                        <Badge variant="outline" className="font-mono">
                          v{instance.version}
                        </Badge>
                      )}
                      {instance.verifiedAt ? (
                        <Badge
                          variant="outline"
                          className="text-emerald-700 dark:text-emerald-400"
                        >
                          Verificado
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Sin verificar</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {[
                        host ? `Host: ${host.name}` : null,
                        instance.mail,
                        instance.url,
                        instance.lastUpdatedAt
                          ? `Update: ${instance.lastUpdatedAt}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Sin detalles"}
                    </p>
                    {instance.dicomEndpoints.length > 0 && (
                      <p className="text-xs font-mono text-muted-foreground">
                        DICOM:{" "}
                        {instance.dicomEndpoints
                          .map(
                            (ep) =>
                              `${ep.aetitle}:${ep.port}${ep.ip ? `@${ep.ip}` : ""}`
                          )
                          .join(", ")}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-9 cursor-pointer"
                      onClick={() => openEdit(instance)}
                      aria-label={`Editar ${typeLabel}`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-9 cursor-pointer"
                      onClick={() => void handleDelete(instance)}
                      disabled={remove.isPending}
                      aria-label={`Eliminar ${typeLabel}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <SoftwareInstanceFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        instance={editing}
        softwareTypes={softwareTypes}
        hosts={hosts}
        onSubmit={handleSubmit}
        isSaving={create.isPending || update.isPending}
      />
    </Card>
  );
}
